import React from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Check } from 'lucide-react';

const solutionDetails = {
  'esg-management': {
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200',
    features: ['Real-time data collection', 'Automated data validation', 'Custom metrics tracking', 'Stakeholder reporting'],
    benefits: ['Improved ESG performance', 'Better stakeholder engagement', 'Reduced reporting time', 'Enhanced transparency'],
    integrations: ['SAP', 'Oracle', 'Salesforce', 'Microsoft Dynamics'],
  },
  'sustainability-reporting': {
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1200',
    features: ['Automated report generation', 'Multiple framework support', 'Data visualization', 'Audit trail'],
    benefits: ['Regulatory compliance', 'Time savings', 'Accuracy improvement', 'Stakeholder trust'],
    integrations: ['GRI', 'SASB', 'TCFD', 'CDP'],
  },
  'corporate-analysis': {
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200',
    features: ['AI-powered analytics', 'Risk assessment', 'Peer comparison', 'Trend analysis'],
    benefits: ['Better decision making', 'Risk mitigation', 'Competitive advantage', 'Strategic planning'],
    integrations: ['Bloomberg', 'Reuters', 'FactSet', 'S&P Global'],
  },
  'net-zero': {
    image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&q=80&w=1200',
    features: ['Emission tracking', 'Reduction planning', 'Science-based targets', 'Progress monitoring'],
    benefits: ['Carbon reduction', 'Cost savings', 'Regulatory compliance', 'Brand reputation'],
    integrations: ['GHG Protocol', 'Science Based Targets', 'CDP', 'Climate Action 100+'],
  },
};

export default function SolutionDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useLanguage();
  
  const solution = solutionDetails[slug as keyof typeof solutionDetails];

  if (!solution) {
    return <div>Solution not found</div>;
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <div className="relative h-96">
        <img
          src={solution.image}
          alt={t(`solutions.${slug}.title`)}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 to-green-800/70" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold text-white mb-4">
              {t(`solutions.${slug}.title`)}
            </h1>
            <p className="text-xl text-white/90 max-w-2xl">
              {t(`solutions.${slug}.description`)}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Features */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('solution.features')}
            </h2>
            <ul className="space-y-4">
              {solution.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <Check className="h-5 w-5 text-green-800 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Benefits */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('solution.benefits')}
            </h2>
            <ul className="space-y-4">
              {solution.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <Check className="h-5 w-5 text-green-800 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Integrations */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('solution.integrations')}
            </h2>
            <ul className="space-y-4">
              {solution.integrations.map((integration, index) => (
                <li key={index} className="flex items-start">
                  <Check className="h-5 w-5 text-green-800 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">{integration}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <button className="btn-primary">
            {t('solution.getStarted')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}